// Growing Circle — internal route tree (English canonical URLs).
//
// Mounted by the canonical <App /> under the parent route
//   <Route path="/growing-circle/*" element={<GrowingCircleShell />} />
//
// All URL path segments after /growing-circle/ are English canonical
// (login, activate, member-area, podcasts, testimonials, meetings,
// profile, admin/verifications, …). The Italian legacy slugs
// (accedi/attiva/spazio/ascolti/testimonianze/incontri/profilo,
// admin/verifiche/utenti/contenuti/testimonianze/incontri and
// richiedi-accesso) were removed on 15 Sep 2026 because the Growing
// Circle has no live production users, no external bookmarks and no
// third-party integration referencing those paths, so a compatibility
// redirect would only preserve dead surface area.

import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/modules/growing-circle/context/AuthContext";
import Landing from "@/modules/growing-circle/pages/Landing";
import AccessRequest from "@/modules/growing-circle/pages/AccessRequest";
import Activate from "@/modules/growing-circle/pages/Activate";
import Login from "@/modules/growing-circle/pages/Login";
import ResetPassword from "@/modules/growing-circle/pages/ResetPassword";
import Dashboard from "@/modules/growing-circle/pages/Dashboard";
import Podcasts from "@/modules/growing-circle/pages/Podcasts";
import ContentDetail from "@/modules/growing-circle/pages/ContentDetail";
import Testimonies from "@/modules/growing-circle/pages/Testimonies";
import Meetings from "@/modules/growing-circle/pages/Meetings";
import Profile from "@/modules/growing-circle/pages/Profile";
import MemberLayout from "@/modules/growing-circle/components/MemberLayout";
import AdminLayout from "@/modules/growing-circle/pages/admin/AdminLayout";
import AdminVerifications from "@/modules/growing-circle/pages/admin/AdminVerifications";
import AdminUsers from "@/modules/growing-circle/pages/admin/AdminUsers";
import AdminContent from "@/modules/growing-circle/pages/admin/AdminContent";
import AdminTestimonies from "@/modules/growing-circle/pages/admin/AdminTestimonies";
import AdminMeetings from "@/modules/growing-circle/pages/admin/AdminMeetings";
import AdminAnalytics from "@/modules/growing-circle/pages/admin/AdminAnalytics";
import AdminAcceptances from "@/modules/growing-circle/pages/admin/AdminAcceptances";
import AdminArticles from "@/modules/growing-circle/pages/admin/AdminArticles";
import AdminArticleEditor from "@/modules/growing-circle/pages/admin/AdminArticleEditor";
import AdminArticlePreview from "@/modules/growing-circle/pages/admin/AdminArticlePreview";
import AdminArticleComments from "@/modules/growing-circle/pages/admin/AdminArticleComments";
import MemberArticleList from "@/modules/growing-circle/pages/articles/MemberArticleList";
import MemberArticleDetail from "@/modules/growing-circle/pages/articles/MemberArticleDetail";
import { AcceptanceProvider } from "@/modules/growing-circle/context/AcceptanceContext";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p className="font-heading text-2xl text-muted-foreground animate-pulse" data-testid="gc-loading">
        One moment…
      </p>
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, ready } = useAuth();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/growing-circle/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/growing-circle/member-area" replace />;
  return children;
}

export default function GrowingCircleRouter() {
  // All paths RELATIVE — parent match is "/growing-circle" from the canonical App.
  return (
    <Routes>
      <Route index element={<Landing />} />
      <Route path="request-access" element={<AccessRequest />} />
      <Route path="activate" element={<Activate />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="login" element={<Login />} />

      <Route element={<Protected><AcceptanceProvider><MemberLayout /></AcceptanceProvider></Protected>}>
        <Route path="member-area" element={<Dashboard />} />
        <Route path="podcasts" element={<Podcasts />} />
        <Route path="podcasts/:id" element={<ContentDetail />} />
        <Route path="testimonials" element={<Testimonies />} />
        <Route path="meetings" element={<Meetings />} />
        <Route path="profile" element={<Profile />} />
        <Route path="articles" element={<MemberArticleList />} />
        <Route path="articles/:slug" element={<MemberArticleDetail />} />
      </Route>

      <Route path="admin" element={<Protected roles={["admin"]}><AdminLayout /></Protected>}>
        <Route index element={<Navigate to="/growing-circle/admin/verifications" replace />} />
        <Route path="verifications" element={<AdminVerifications />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="content" element={<AdminContent />} />
        <Route path="testimonials" element={<AdminTestimonies />} />
        <Route path="meetings" element={<AdminMeetings />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="acceptances" element={<AdminAcceptances />} />
        <Route path="articles" element={<AdminArticles />} />
        <Route path="articles/new" element={<AdminArticleEditor />} />
        <Route path="articles/:id/edit" element={<AdminArticleEditor />} />
        <Route path="articles/:id/comments" element={<AdminArticleComments />} />
      </Route>

      {/* Admin preview lives OUTSIDE the AdminLayout so the article surface fills the full viewport. */}
      <Route path="admin/articles/:id/preview" element={<Protected roles={["admin"]}><AdminArticlePreview /></Protected>} />

      {/* Anything else under /growing-circle/* redirects to the Circle landing */}
      <Route path="*" element={<Navigate to="/growing-circle" replace />} />
    </Routes>
  );
}
